import React, {
  useState, useCallback, useEffect, useMemo,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import dayjs from 'dayjs';
import dayjsPluginUTC from 'dayjs-plugin-utc';
import { useAnalytics } from 'use-analytics';
import _ from 'lodash';
import { flightSearch, updateFlightSearchObj } from '../../../store/slices/flight';
import FlightList from './FlightList';
import Modal from '../../Modal';
import {
  SearchStatus,
  InfoPageSize,
  ListPageSize,
  FlightSortingValues,
  FiltersInitialValue,
  LocationTypes,
  TravelTypes,
  FlightSearchTypes,
  FlightSortingType,
} from '../../../constants';
import Plane from '../../../assets/svgs/plane.svg';
import SortingFilter from '../../Filters/SortingFilter';
import SelectedFlight from './SelectedFlight';
import ModalContent from './ModalContent';
import convertGeonameToAutocompleteItem from '../../../helpers/convertGeonameToAutocompleteItem';
import isFlightSegmentsValid from '../../../helpers/validationHelpers/isFlightSegmentsValid';
import FlightSearchBar from './FlightSearchBar';
import FlightFilterBar from './FlightFilterBar/index';

dayjs.extend(dayjsPluginUTC);

const FlightInformation = ({
  cruiseItineraryDetail,
  flightItinerary,
  tripId,
  flightLeaveDate,
  flightReturnDate,
  doShowAsModalOnly,
  onModalClose,
  doAllowEmptyOrigin,
  doCheckoutOnSelect,
  cruiseItineraryId,
  travelOption,
}) => {
  const dispatch = useDispatch();
  const { track } = useAnalytics();
  const [isModalOpen, setIsModalOpen] = useState(doShowAsModalOnly);
  const { flightRateCodes, travelAgency } = useSelector((store) => store.user);

  const doHidePostSearchFilters = travelAgency && travelAgency.settings
    && travelAgency.settings.doHidePostSearchFilters;
  const flightSearchState = useSelector((store) => store.flight.flightSearches[tripId]);
  const flightSearchResponse = useSelector((state) => flightSearchState
    && state.flight.responses && state.flight.responses[flightSearchState.queryString]);

  let cruiseDepartureCityId = travelOption && travelOption.itineraryDepartCityId;
  let cruiseReturnCityId = travelOption && travelOption.itineraryReturnCityId;
  let cruiseDepartureCityName = travelOption && travelOption.itineraryDepartCity;
  let cruiseReturnCityName = travelOption && travelOption.itineraryReturnCity;
  let cruiseItineraryRow = travelOption && travelOption.itineraryRows && travelOption
    .itineraryRows.find((i) => i.itineraryId === cruiseItineraryId);

  if (cruiseItineraryDetail) {
    cruiseDepartureCityId = cruiseItineraryDetail.itineraryDepartCityId;
    cruiseReturnCityId = cruiseItineraryDetail.itineraryReturnCityId;
    cruiseDepartureCityName = cruiseItineraryDetail.itineraryDepartCity;
    cruiseReturnCityName = cruiseItineraryDetail.itineraryReturnCity;
    cruiseItineraryRow = cruiseItineraryDetail;
  }

  const searchbarFlightOrigin = useSelector((store) => {
    if (travelOption) {
      if (travelOption.mode === TravelTypes.hotel) return store.tripSearch.searchObj.hotel.origin;
      if (travelOption.mode === TravelTypes.carrental) return store.tripSearch.searchObj.car.origin;
    }
    return store.tripSearch.searchObj.cruise.filters.origin;
  });

  const cruiseDepartureCountry = travelOption && (travelOption.itineraryDepartureCountryCode
    || (((travelOption.hotelItinerary && travelOption.hotelItinerary.searchGeoname)
    || (travelOption.carItinerary && travelOption.carItinerary.originGeoname)) || {}).countryCode);

  const isDomesticFlight = cruiseDepartureCountry && searchbarFlightOrigin
    && cruiseDepartureCountry === searchbarFlightOrigin.countryCode;

  const leaveDate = flightLeaveDate
    || (flightItinerary && flightItinerary.flightSegments[0].departureDateTime)
    || dayjs((cruiseItineraryRow || {}).itineraryDepartDate
      || (travelOption && travelOption.startDateTime)).subtract(isDomesticFlight ? 0 : 1, 'day').format('YYYY-MM-DD');

  const returnDate = flightReturnDate
    || (flightItinerary && (flightItinerary.flightSegments[1]
      ? flightItinerary.flightSegments[1].departureDateTime
      : flightItinerary.flightSegments[0].arrivalDateTime))
    || (cruiseItineraryRow || {}).itineraryReturnDate
    || (travelOption && travelOption.endDateTime);
  const selectedTrips = useSelector((store) => store.trip.selectedTrips);

  const handleOpenModal = useCallback(() => {
    setIsModalOpen(true);
    track('flightInformation.handleOpenModal');
  }, [track]);

  const handleCloseModal = useCallback(() => {
    setIsModalOpen(false);
    if (onModalClose) {
      onModalClose(null);
    }
    track('flightInformation.handleCloseModal');
  }, [onModalClose, track]);

  useEffect(() => {
    /// OUTBOUND FLIGHT DESTINATION
    let flightDestination = cruiseDepartureCityId && cruiseDepartureCityName ? {
      value: cruiseDepartureCityId,
      label: cruiseDepartureCityName,
      group: 'Port of departure',
      locationType: LocationTypes.PORT_OF_DEPARTURE,
    } : null;

    if (!flightDestination && travelOption) {
      if (travelOption.mode === TravelTypes.hotel) {
        const hotelGeoname = travelOption.hotelItinerary
          && travelOption.hotelItinerary.searchGeoname;
        if (hotelGeoname) {
          flightDestination = {
            latitude: hotelGeoname.latitude,
            longitude: hotelGeoname.longitude,
            label: hotelGeoname.name,
          };
        }
      } else if (travelOption.mode === TravelTypes.carrental) {
        flightDestination = travelOption.carItinerary.originGeoname.iata ? {
          airportCode: travelOption.carItinerary.originGeoname.iata,
          label: travelOption.carItinerary.originGeoname.name,
          city: travelOption.carItinerary.originGeoname.city,
        } : {
          latitude: travelOption.carItinerary.originGeoname.latitude,
          longitude: travelOption.carItinerary.originGeoname.longitude,
          label: travelOption.carItinerary.originGeoname.name,
          city: travelOption.carItinerary.originGeoname.city,
        };
      }
    }

    /// INBOUND FLIGHT ORIGIN
    let cruiseReturnPort = null;
    if (cruiseDepartureCityId && cruiseReturnCityId
      && cruiseDepartureCityId !== cruiseReturnCityId) {
      cruiseReturnPort = {
        value: cruiseReturnCityId,
        label: cruiseReturnCityName,
        group: 'Port of departure',
        locationType: LocationTypes.PORT_OF_DEPARTURE,
      };
    }

    let rentalCarReturnCity = null;
    if (travelOption && travelOption.mode === TravelTypes.carrental
      && travelOption.carItinerary.originGeoname.name
      !== travelOption.carItinerary.destinationGeoname.name) {
      rentalCarReturnCity = travelOption.carItinerary.destinationGeoname.iata ? {
        airportCode: travelOption.carItinerary.destinationGeoname.iata,
        label: travelOption.carItinerary.destinationGeoname.name,
        city: travelOption.carItinerary.destinationGeoname.city,
      } : {
        latitude: travelOption.carItinerary.destinationGeoname.latitude,
        longitude: travelOption.carItinerary.destinationGeoname.longitude,
        label: travelOption.carItinerary.destinationGeoname.name,
        city: travelOption.carItinerary.destinationGeoname.city,
      };
    }

    /// FORMAT FLIGHT SEGMENTS
    let flightSegments = null;
    let flightSearchType = FlightSearchTypes.ROUND_TRIP;

    if (flightItinerary) {
      flightSegments = [{
        origin: convertGeonameToAutocompleteItem(
          flightItinerary.flightSegments[0].originGeoname,
        ),
        destination: convertGeonameToAutocompleteItem(
          flightItinerary.flightSegments[0].destinationGeoname,
        ),
      }];

      if (flightItinerary.flightSegments[1]) {
        flightSegments.push({
          origin: convertGeonameToAutocompleteItem(
            flightItinerary.flightSegments[1].originGeoname,
          ),
          destination: convertGeonameToAutocompleteItem(
            flightItinerary.flightSegments[1].destinationGeoname,
          ),
        });
      }

      if (flightItinerary.flightSegments[1]
        && flightItinerary.flightSegments[0].destinationGeoname.iata
        !== flightItinerary.flightSegments[1].originGeoname.iata) {
        flightSearchType = FlightSearchTypes.MULTI_CITY;
      } else if (!flightItinerary.flightSegments[1]) {
        flightSearchType = FlightSearchTypes.ONE_WAY;
      }
    } else if ((flightSearchState || {}).searchObj && flightSearchState.searchObj.flightSegments) {
      flightSegments = JSON.parse(JSON.stringify(flightSearchState.searchObj.flightSegments));
      flightSearchType = flightSearchState.searchObj.flightSearchType;
    } else {
      flightSegments = [{
        origin: searchbarFlightOrigin,
        destination: flightDestination,
      }];
      if (cruiseReturnPort || rentalCarReturnCity) {
        flightSegments[1] = {
          origin: cruiseReturnPort || rentalCarReturnCity,
          destination: searchbarFlightOrigin,
        };
        flightSearchType = FlightSearchTypes.MULTI_CITY;
      } else {
        flightSegments[1] = {
          origin: flightSegments[0].destination,
          destination: flightSegments[0].origin,
        };
      }
    }

    const leaveDateObj = flightSearchState && flightSearchState.searchObj
      ? flightSearchState.searchObj.leaveDate : leaveDate;
    const returnDateObj = flightSearchState && flightSearchState.searchObj
      ? flightSearchState.searchObj.returnDate : returnDate;

    if (!flightSegments[0].leaveDate) {
      flightSegments[0].leaveDate = leaveDateObj;

      if (flightSearchType === FlightSearchTypes.ROUND_TRIP
      || flightSearchType === FlightSearchTypes.MULTI_CITY) {
        if (!flightSegments[1]) {
          flightSegments.push({ leaveDate: returnDateObj });
        } else {
          flightSegments[1].leaveDate = returnDateObj;
        }
      }
    }

    if (flightSearchType === FlightSearchTypes.ROUND_TRIP && flightSegments[1]) {
      flightSegments[1].origin = undefined;
      flightSegments[1].destination = undefined;
    }

    const commonSearchObj = {
      pageSize: isModalOpen ? ListPageSize : InfoPageSize,
      flightSegments,
      flightSearchType,
    };

    if ((!flightSearchState || !flightSearchState.searchObj)
    && flightRateCodes && flightRateCodes.length) {
      const filteredRateCodes = _.filter(flightRateCodes, (r) => r.isSelectedByDefault);
      if (filteredRateCodes.length > 0)commonSearchObj.rateCodes = _.map(filteredRateCodes, 'rateCode');
    }
    if (!isModalOpen) {
      commonSearchObj.page = 1;
    }
    const hasSelectedFlightItinerary = !!(selectedTrips && selectedTrips[tripId]
      && selectedTrips[tripId].flightItinerary);
    if (!hasSelectedFlightItinerary && isFlightSegmentsValid(commonSearchObj
      .flightSegments, flightSearchType)) {
      dispatch(flightSearch({
        tripId,
        searchObj: commonSearchObj,
        defaultSearchObj: flightSearchState && flightSearchState.defaultSearchObj
          ? flightSearchState.defaultSearchObj : commonSearchObj,
      }));
      track('flightInformation.startFlightSearch', {
        tripId,
        searchObj: JSON.stringify(commonSearchObj),
      });
    } else if (!hasSelectedFlightItinerary) {
      dispatch(updateFlightSearchObj({
        tripId,
        searchObj: commonSearchObj,
        defaultSearchObj: flightSearchState && flightSearchState.defaultSearchObj
          ? flightSearchState.defaultSearchObj : commonSearchObj,
        searchStatus: SearchStatus.DONE,
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isModalOpen]);

  const handleSearch = useCallback((searchObj) => {
    dispatch(flightSearch({ tripId, searchObj }));
  }, [dispatch, tripId]);

  const flightSortingValues = useMemo(() => (
    flightSearchState
    && flightSearchState.searchObj
    && flightSearchState.searchObj.flightSearchType === FlightSearchTypes.ONE_WAY
      ? _.filter(FlightSortingValues,
        (fs) => fs.value !== FlightSortingType.INBOUND_DURATION) : FlightSortingValues),
  [flightSearchState]);

  const SortingFilterElement = (
    flightSearchState
    && (
      <SortingFilter
        sortingValues={flightSortingValues}
        initialValue={FiltersInitialValue.flight.sortBy}
        value={flightSearchState.searchObj.sortBy}
        direction={flightSearchState.searchObj.sortDirection}
        onSortChange={handleSearch}
        variant="transparentOutline"
      />
    )
  );

  const doShowFilters = flightSearchResponse && flightSearchState
  && (flightSearchState.airlinesAutocompleteResults || []).length > 0
  && flightSearchResponse.doHaveResultsWithoutFilter;

  return (
    <>
      {!doCheckoutOnSelect && (
        <SelectedFlight tripId={tripId} onModalClose={onModalClose} isModalOpen={isModalOpen} />
      )}
      {searchbarFlightOrigin || doAllowEmptyOrigin
        ? (
          <>
            {!doShowAsModalOnly
              && (
                <>
                  <FlightSearchBar tripId={tripId} />
                  {(doShowFilters && !doHidePostSearchFilters) ? (
                    <>
                      <div className="font-bold">
                        Filter by
                      </div>
                      <FlightFilterBar tripId={tripId} />
                    </>
                  ) : <></>}

                  {doShowFilters && (
                  <div className="mt-10 mb-5 font-bold flex justify-between items-center">
                    <span className="font-subHeading text-subHeading">
                      Best flights
                    </span>
                    {SortingFilterElement}
                  </div>
                  )}
                  <FlightList
                    onViewMoreClick={handleOpenModal}
                    tripId={tripId}
                    doCheckoutOnSelect={doCheckoutOnSelect}
                  />
                </>
              )}
            <Modal
              open={isModalOpen}
              onClose={handleCloseModal}
            >
              <ModalContent
                tripId={tripId}
                onModalClose={onModalClose}
                onCloseModal={handleCloseModal}
                doCheckoutOnSelect={doCheckoutOnSelect}
                isModalOpen={isModalOpen}
                SortingFilterElement={SortingFilterElement}
              />
            </Modal>
          </>
        )
        : (
          <div className="flex items-center flex-col h-40 justify-center gap-3 text-center">
            <Plane />
            <span className="font-bold">
              No flights found
            </span>
            Please select flight origin.
          </div>
        )}
    </>
  );
};

FlightInformation.propTypes = {
  cruiseItineraryDetail: PropTypes.shape(),
  flightItinerary: PropTypes.shape(),
  cruiseItineraryId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  tripId: PropTypes.string.isRequired,
  flightLeaveDate: PropTypes.string,
  flightReturnDate: PropTypes.string,
  doShowAsModalOnly: PropTypes.bool,
  onModalClose: PropTypes.func,
  doAllowEmptyOrigin: PropTypes.bool,
  doCheckoutOnSelect: PropTypes.bool,
  travelOption: PropTypes.shape({
    key: PropTypes.string,
    mode: PropTypes.number,
    itineraryDepartCity: PropTypes.string,
    itineraryDepartCityId: PropTypes.string,
    itineraryReturnCityId: PropTypes.string,
    itineraryReturnCity: PropTypes.string,
    itineraryDepartureCountryCode: PropTypes.string,
    startDateTime: PropTypes.string,
    endDateTime: PropTypes.string,
    itinerary: PropTypes.shape(),
    itineraryRows: PropTypes.arrayOf(PropTypes.shape()),
    flightItinerary: PropTypes.shape({
      flightSegments: PropTypes.arrayOf(PropTypes.shape()),
    }),
    hotelItinerary: PropTypes.shape({
      id: PropTypes.number,
      searchGeoname: PropTypes.shape(),
    }),
    carItinerary: PropTypes.shape({
      id: PropTypes.number,
      destinationGeoname: PropTypes.shape(),
      originGeoname: PropTypes.shape(),
    }),
  }).isRequired,
};

FlightInformation.defaultProps = {
  cruiseItineraryDetail: undefined,
  flightItinerary: undefined,
  cruiseItineraryId: undefined,
  flightLeaveDate: undefined,
  flightReturnDate: undefined,
  doShowAsModalOnly: false,
  onModalClose: undefined,
  doAllowEmptyOrigin: true,
  doCheckoutOnSelect: false,
};
export default React.memo(FlightInformation);
